// Global Instructions Rule Applied!

import { useCallback, useEffect, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { fetchFundWaterfall } from '../services/fundsService.ts'
import { FundWaterfallRecord } from '../../../state_manager/data_models/fund_waterfall_record.ts'

/**
 * useFundWaterfall — the fund's open distribution waterfall (header, tiers
 * with their splits, GP carry participants, the booked-distribution lock)
 * for the Terms tab Waterfall section, with a refetch run after every save.
 *
 * Section-local detail, like the statement settings: not in activeRecords.
 */
export function useFundWaterfall(fundId: string | null | undefined) {
  const fundIdString = fundId ? String(fundId) : null

  const [waterfall, setWaterfall] = useState<FundWaterfallRecord | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!fundIdString) {
      setWaterfall(null)
      setLoading(false)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const accessToken = getAccessToken(store.getState().auth)
      const res = await fetchFundWaterfall(fundIdString, accessToken)
      if (res.error) setError(res.error)
      if (res.data) setWaterfall(res.data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load the waterfall')
    } finally {
      setLoading(false)
    }
  }, [fundIdString])

  useEffect(() => {
    load()
  }, [load])

  return { waterfall, loading, error, refetch: load }
}
